import { useEffect, useMemo, useState } from 'react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { useAuth } from '../providers/AuthProvider'
import { createTmaApiClient } from '../services/tmaApiClient'
import type { TmaAuthUser } from '../services/telegramAuth'

type GroupStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

const IMPERSONATION_TELEGRAM_IDS = new Set(['470227422'])

interface FlowListItem {
  flowNo: string
  lastStartAt: string | null
}

interface FlowsPayload {
  flows?: Array<{
    flowNo?: string
    lastStartAt?: string | null
  }>
}

interface MatrixLesson {
  id: string
  lessonNo: number
  date: string | null
}

interface MatrixCell {
  lessonId: string
  lessonNo: number
  markLabel?: string | null
}

interface MatrixStudentRow {
  id: string
  fullName: string
  phone: string | null
  cells?: MatrixCell[]
}

interface MatrixPayload {
  flowNo: string
  lessons?: MatrixLesson[]
  students: MatrixStudentRow[]
}

interface GroupStudent {
  id: string
  firstName: string
  lastName: string
  phone: string | null
}

const parseIsoTime = (value: string | null | undefined): number | null => {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return null
  }

  const timestamp = Date.parse(raw)
  return Number.isNaN(timestamp) ? null : timestamp
}

const pickDefaultFlowNo = (flows: FlowListItem[]): string => {
  if (!flows.length) {
    return ''
  }

  const now = Date.now()
  const started = flows
    .map((flow) => ({
      flowNo: flow.flowNo,
      ts: parseIsoTime(flow.lastStartAt),
    }))
    .filter((item): item is { flowNo: string; ts: number } => typeof item.ts === 'number' && item.ts <= now)
    .sort((left, right) => right.ts - left.ts)

  if (started.length) {
    return started[0].flowNo
  }

  return [...flows].sort((left, right) => Number(right.flowNo) - Number(left.flowNo))[0].flowNo
}

const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const normalized = fullName.trim()
  if (!normalized) {
    return {
      firstName: 'Без имени',
      lastName: '—',
    }
  }

  const parts = normalized.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return {
      firstName: parts[0],
      lastName: '—',
    }
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

const normalizePhoneToPlus7 = (value: string): string => {
  const cleaned = value.trim()
  const digits = cleaned.replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`
  }

  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`
  }

  if (digits.length === 10) {
    return `+7${digits}`
  }

  if (cleaned.startsWith('+')) {
    return `+${digits}`
  }

  return `+${digits}`
}

const sanitizePhone = (value: string | null | undefined): string | null => {
  const normalized = normalizePhoneToPlus7(String(value ?? ''))
  return normalized || null
}

const displayMarkLabel = (markLabel: string | null | undefined): string => {
  if (!markLabel || markLabel === '—') {
    return '—'
  }

  return markLabel === '+' ? 'П' : markLabel
}

const phoneForTelLink = (value: string): string => normalizePhoneToPlus7(value)

const escapeVCard = (value: string): string => value.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;')

const buildVCard = (student: GroupStudent): string => {
  const fullName = `${student.firstName} ${student.lastName === '—' ? '' : student.lastName}`.trim()
  const phone = student.phone ? phoneForTelLink(student.phone) : ''

  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${escapeVCard(student.lastName === '—' ? '' : student.lastName)};${escapeVCard(student.firstName)};;;`,
    `FN:${escapeVCard(fullName)}`,
    phone ? `TEL;TYPE=CELL:${phone}` : '',
    'END:VCARD',
  ]
    .filter(Boolean)
    .join('\r\n')
}

const saveContactToDevice = (student: GroupStudent) => {
  const fullName = `${student.firstName} ${student.lastName === '—' ? '' : student.lastName}`.trim()
  const confirmed = window.confirm(`Сохранить контакт «${fullName}» в телефон?`)

  if (!confirmed) {
    return
  }

  const vCard = buildVCard(student)
  const blob = new Blob([vCard], { type: 'text/vcard;charset=utf-8' })
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  const fileSafeName = fullName.replace(/[^\p{L}\p{N}_-]+/gu, '_')

  link.href = objectUrl
  link.download = `${fileSafeName || 'contact'}.vcf`
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

export const StudentHome = ({ user }: { user: TmaAuthUser }) => {
  const auth = useAuth()
  const [status, setStatus] = useState<GroupStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [students, setStudents] = useState<GroupStudent[]>([])
  const [matrix, setMatrix] = useState<MatrixPayload | null>(null)
  const [flows, setFlows] = useState<FlowListItem[]>([])
  const [activeFlowNo, setActiveFlowNo] = useState<string>(user.flowNo?.trim() || '')
  const [selectedStudentId, setSelectedStudentId] = useState<string>('')

  const apiClient = useMemo(() => createTmaApiClient(auth.session), [auth.session])
  const isImpersonationEnabled =
    auth.status === 'authenticated'
    && auth.session?.user.role === 'student'
    && IMPERSONATION_TELEGRAM_IDS.has(auth.session.user.telegramId)

  useEffect(() => {
    const session = auth.session

    if (auth.status !== 'authenticated' || !session) {
      setStatus('error')
      setErrorMessage('Раздел «Группа» недоступен: отсутствует активная student session.')
      return
    }

    if (session.user.role !== 'student') {
      setStatus('error')
      setErrorMessage('Раздел «Группа» доступен только для роли студента.')
      return
    }

    let cancelled = false

    const loadFlowAccess = async () => {
      const flowNo = session.user.flowNo?.trim() || user.flowNo?.trim() || ''

      if (!isImpersonationEnabled) {
        setFlows(flowNo ? [{ flowNo, lastStartAt: null }] : [])
        setActiveFlowNo(flowNo)
        if (!flowNo) {
          setStudents([])
          setMatrix(null)
          setStatus('empty')
        }
        return
      }

      setStatus('loading')
      setErrorMessage(null)

      const flowsResult = await apiClient.request({
        method: 'GET',
        path: '/ops/schedule/flows',
      })

      if (cancelled) {
        return
      }

      if (!flowsResult.ok || !flowsResult.responsePayload || typeof flowsResult.responsePayload !== 'object') {
        setStatus('error')
        setErrorMessage(`Не удалось загрузить список потоков (${flowsResult.status ?? 'n/a'}).`)
        return
      }

      const flowsPayload = flowsResult.responsePayload as FlowsPayload
      const flowItems = (flowsPayload.flows ?? [])
        .map((item) => {
          const normalizedFlowNo = String(item.flowNo ?? '').trim()
          if (!normalizedFlowNo) {
            return null
          }

          return {
            flowNo: normalizedFlowNo,
            lastStartAt: typeof item.lastStartAt === 'string' ? item.lastStartAt : null,
          }
        })
        .filter((item): item is FlowListItem => Boolean(item))

      setFlows(flowItems)

      if (!flowItems.length) {
        setActiveFlowNo('')
        setStudents([])
        setMatrix(null)
        setStatus('empty')
        return
      }

      setActiveFlowNo((current) => (current && flowItems.some((flow) => flow.flowNo === current)
        ? current
        : pickDefaultFlowNo(flowItems)))
    }

    void loadFlowAccess()

    return () => {
      cancelled = true
    }
  }, [apiClient, auth.session, auth.status, isImpersonationEnabled, user.flowNo])

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session || auth.session.user.role !== 'student') {
      return
    }

    if (!activeFlowNo) {
      return
    }

    let cancelled = false

    const loadGroup = async () => {
      setStatus('loading')
      setErrorMessage(null)

      const result = await apiClient.request({
        method: 'GET',
        path: '/ops/console/attendance/matrix',
        query: { flowNo: activeFlowNo },
      })

      if (cancelled) {
        return
      }

      if (!result.ok || !result.responsePayload || typeof result.responsePayload !== 'object') {
        setStatus('error')
        setErrorMessage(`Не удалось загрузить список группы (${result.status ?? 'n/a'}).`)
        return
      }

      const payload = result.responsePayload as MatrixPayload
      setMatrix(payload)
      const rawStudents = Array.isArray(payload.students) ? payload.students : []

      const mappedStudents = rawStudents
        .map((item) => {
          const { firstName, lastName } = splitFullName(item.fullName)
          return {
            id: item.id,
            firstName,
            lastName,
            phone: sanitizePhone(item.phone),
          }
        })
        .sort((left, right) => {
          const byLastName = left.lastName.localeCompare(right.lastName, 'ru-RU')
          if (byLastName !== 0) {
            return byLastName
          }

          return left.firstName.localeCompare(right.firstName, 'ru-RU')
        })

      setStudents(mappedStudents)
      setSelectedStudentId((current) => {
        if (!isImpersonationEnabled) {
          return ''
        }

        if (current && mappedStudents.some((student) => student.id === current)) {
          return current
        }

        return mappedStudents[0]?.id || ''
      })
      setStatus(mappedStudents.length ? 'ready' : 'empty')
    }

    void loadGroup()

    return () => {
      cancelled = true
    }
  }, [activeFlowNo, apiClient, auth.session, auth.status, isImpersonationEnabled])

  const selectedProfileStudent = useMemo(
    () => matrix?.students.find((student) => student.id === selectedStudentId) ?? null,
    [matrix?.students, selectedStudentId],
  )

  const selectedProfileGroupStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) ?? null,
    [selectedStudentId, students],
  )

  const lessonByNo = useMemo(
    () => new Map((matrix?.lessons ?? []).map((lesson) => [lesson.lessonNo, lesson])),
    [matrix?.lessons],
  )

  return (
    <>
      <h2 className="content-title">Группа</h2>
      <p className="hint">
        Поток: {activeFlowNo || 'не задан'} · Нажмите на строку ученика, чтобы предложить сохранить контакт в телефоне.
      </p>

      {isImpersonationEnabled ? (
        <p className="hint">Режим Олега: можно выбрать поток и ученика для просмотра как student-профиль.</p>
      ) : null}

      {isImpersonationEnabled && status !== 'error' ? (
        <section className="panel teacher-student-profile" aria-label="Student impersonation controls">
          <h3 className="content-title spaced-bottom">Просмотр под учеником</h3>

          <div className="teacher-student-fields is-spaced">
            <label htmlFor="student-flow-select">Поток</label>
            <select
              id="student-flow-select"
              value={activeFlowNo}
              onChange={(event) => {
                setActiveFlowNo(event.target.value)
                setSelectedStudentId('')
              }}
              disabled={!flows.length}
            >
              {!flows.length ? <option value="">Нет потоков</option> : null}
              {flows.map((flow) => (
                <option key={flow.flowNo} value={flow.flowNo}>
                  Поток {flow.flowNo}
                </option>
              ))}
            </select>

            <label htmlFor="student-profile-select">Ученик</label>
            <select
              id="student-profile-select"
              value={selectedStudentId}
              onChange={(event) => setSelectedStudentId(event.target.value)}
              disabled={!students.length}
            >
              {!students.length ? <option value="">Нет учеников</option> : null}
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>

          {selectedProfileStudent && selectedProfileGroupStudent ? (
            <>
              <ul className="list flush-top">
                <li>Имя: {selectedProfileGroupStudent.firstName}</li>
                <li>Фамилия: {selectedProfileGroupStudent.lastName}</li>
                <li>Телефон: {selectedProfileGroupStudent.phone ?? 'Телефон не указан'}</li>
              </ul>

              <div className="group-table-scroll">
                <table className="group-table">
                  <thead>
                    <tr>
                      <th>Урок</th>
                      <th>Дата</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedProfileStudent.cells ?? [])
                      .slice()
                      .sort((left, right) => left.lessonNo - right.lessonNo)
                      .map((cell) => {
                        const lesson = lessonByNo.get(cell.lessonNo)
                        return (
                          <tr key={cell.lessonId}>
                            <td>{cell.lessonNo}</td>
                            <td>{lesson?.date ? new Date(lesson.date).toLocaleString('ru-RU') : '—'}</td>
                            <td>{displayMarkLabel(cell.markLabel)}</td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {status === 'loading' ? <LoadingSpinner label="Загружаем список учеников группы..." /> : null}

      {status === 'error' ? <div className="alert">{errorMessage ?? 'Не удалось загрузить группу.'}</div> : null}

      {status === 'empty' ? <p className="hint">Для этого потока пока нет учеников.</p> : null}

      {status === 'ready' ? (
        <div className="group-table-scroll">
          <table className="group-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Фамилия</th>
                <th>Телефон</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const isClickable = Boolean(student.phone)

                return (
                  <tr
                    key={student.id}
                    className={isClickable ? 'group-row-clickable' : 'group-row-disabled'}
                    tabIndex={isClickable ? 0 : -1}
                    onClick={() => {
                      if (isClickable) {
                        saveContactToDevice(student)
                      }
                    }}
                    onKeyDown={(event) => {
                      if (!isClickable) {
                        return
                      }

                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        saveContactToDevice(student)
                      }
                    }}
                  >
                    <td>{student.firstName}</td>
                    <td>{student.lastName}</td>
                    <td>
                      {student.phone ? (
                        <span className="group-phone-link">{student.phone}</span>
                      ) : (
                        <span className="group-phone-missing">Телефон не указан</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  )
}
